import { AllColors, themeColors } from './colors/colors';
import { agGridDarkColors, globalLightColors } from './colors/agGridColors';
import { createTheme, darken } from '@material-ui/core/styles';

export const sansRegular = ['GT America Regular', 'Helvetica', 'sans-serif'].join(',');
export const sansMedium = ['GT America Medium', 'Helvetica', 'sans-serif'].join(',');
export const sansBold = ['GT America Bold', 'Helvetica', 'sans-serif'].join(',');
export const sansCondensedBold = ['GT America Condensed Bold', 'Helvetica', 'sans-serif'].join(',');

declare module '@material-ui/core/styles/createPalette' {
  interface PaletteOptions {
    tertiary?: PaletteColorOptions;
  }

  interface Palette {
    tertiary: PaletteColor;
  }
}
declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    font: {
      regular: string;
      medium: string;
      bold: string;
      condensedBold: string;
    };
    colors: {
      [P in keyof AllColors]: AllColors[P];
    };
    boxShadows: {
      one: string;
      two: string;
      three: string;
      four: string;
      five: string;
      six: string;
    };
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    font: {
      regular: string;
      medium: string;
      bold: string;
      condensedBold: string;
    };
    colors: {
      [P in keyof AllColors]: AllColors[P];
    };
    boxShadows: {
      one: string;
      two: string;
      three: string;
      four: string;
      five: string;
      six: string;
    };
  }
}

export const noShadow = {
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
  },
  '&:focus': {
    boxShadow: 'none',
  },
  '@media (hover: none)': {
    boxShadow: 'none',
  },
};

const boxShadowsLight = {
  one: '0px 1px 5px rgba(80, 100, 130, 0.45)',
  two: '0px 2px 8px rgba(80, 100, 130, 0.45)',
  three: '0px 4px 10px rgba(80, 100, 130, 0.4)',
  four: '0px 4px 16px rgba(80, 100, 130, 0.4)',
  five: '0px 8px 20px rgba(80, 100, 130, 0.4)',
  six: '0px 12px 26px rgba(80, 100, 130, 0.4)',
};

const materialShadowsLight = {
  one: '0px 2px 1px -1px rgba(80, 100, 130,0.2),0px 1px 1px 0px rgba(80, 100, 130,0.14),0px 1px 3px 0px rgba(80, 100, 130,0.12)',
  two: '0px 3px 1px -2px rgba(80, 100, 130,0.2),0px 2px 2px 0px rgba(80, 100, 130,0.14),0px 1px 5px 0px rgba(80, 100, 130,0.12)',
  three:
    '0px 3px 3px -2px rgba(80, 100, 130,0.2),0px 3px 4px 0px rgba(80, 100, 130,0.14),0px 1px 8px 0px rgba(80, 100, 130,0.12)',
  four: '0px 2px 4px -1px rgba(80, 100, 130,0.2),0px 4px 5px 0px rgba(80, 100, 130,0.14),0px 1px 10px 0px rgba(80, 100, 130,0.12)',
  five: '0px 3px 5px -1px rgba(80, 100, 130,0.2),0px 5px 8px 0px rgba(80, 100, 130,0.14),0px 1px 14px 0px rgba(80, 100, 130,0.12)',
  six: '0px 3px 5px -1px rgba(80, 100, 130,0.2),0px 6px 10px 0px rgba(80, 100, 130,0.14),0px 1px 18px 0px rgba(80, 100, 130,0.12)',
};

const materialShadowsDark = {
  one: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
  two: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
  three:
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
  four: '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
  five: '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
  six: '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
};
const boxShadowsDark = {
  one: '0px 1px 5px rgba(0, 0, 0, 0.7)',
  two: '0px 2px 8px rgba(0, 0, 0, 0.7)',
  three: '0px 4px 10px rgba(0, 0, 0, 0.7)',
  four: '0px 4px 16px rgba(0, 0, 0, 0.7)',
  five: '0px 8px 20px rgba(0, 0, 0, 0.7)',
  six: '0px 12px 26px rgba(0, 0, 0, 0.7)',
};

const shadows = {
  light: boxShadowsLight,
  dark: boxShadowsDark,
};

const materialShadows = {
  light: materialShadowsLight,
  dark: materialShadowsDark,
};

export const getTheme = (type: 'light' | 'dark' = 'light') => {
  const colors: AllColors = themeColors[type];
  const boxShadows = shadows[type];
  const materialBoxShadows = materialShadows[type];
  const theme = createTheme({
    palette: {
      type,
      background: {
        default: colors.backgroundColorBody,
        paper: colors.backgroundColorPaper,
      },
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      tertiary: {
        main: colors.white,
        dark: darken(colors.white, 0.35),
        contrastText: colors.slateBase,
      },
      error: {
        main: colors.danger,
      },
      success: {
        main: colors.success,
      },
      warning: {
        main: colors.warning,
      },
      text: {
        primary: colors.textBase,
      },
      action: {
        disabled: colors.disabled,
        disabledBackground: colors.disabledBackground,
      },
    },
    font: {
      regular: sansRegular,
      medium: sansMedium,
      bold: sansBold,
      condensedBold: sansCondensedBold,
    },
    colors,
    boxShadows,
    typography: {
      fontFamily: sansRegular,
      htmlFontSize: 10,
    },
    shape: {
      borderRadius: 3,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          'strong, b': {
            fontFamily: sansMedium,
            fontWeight: 'normal',
          },
          '.ag-theme-tock': {
            ...globalLightColors,
          },
          '.DarkMode .ag-theme-tock': {
            ...agGridDarkColors,
          },
        },
      },
      // Style sheet name ⚛️
      MuiButton: {
        // Name of the rule
        root: {
          ...noShadow,
        },
        contained: {
          ...noShadow,
          padding: '4px 16px',
        },
        outlined: {
          padding: '3px 16px',
        },
        text: {
          padding: '4px 8px',
        },
        containedPrimary: {
          ...noShadow,
        },
        containedSecondary: {
          ...noShadow,
        },
        outlinedSizeSmall: {
          padding: '4px 9px',
        },
        containedSizeSmall: {
          padding: '5px 10px',
        },
        textSizeSmall: {
          padding: '5px 10px',
        },
        outlinedSizeLarge: {
          padding: '7px 21px',
        },
        containedSizeLarge: {
          padding: '8px 22px',
        },
        textSizeLarge: {
          padding: '8px 22px',
        },
        label: {
          textTransform: 'none',
          fontFamily: sansMedium,
        },
      },
      MuiFormHelperText: {
        root: {
          color: colors.textLight,
          lineHeight: 1.33, // same as caption typography

          '&$disabled': {
            color: colors.textLight,
          },
        },
      },
      MuiTableCell: {
        root: {
          padding: '12px',
          verticalAlign: 'text-top',
        },
        head: {
          color: colors.textBase,
          fontFamily: sansMedium,
          verticalAlign: 'middle',
        },
        alignRight: {
          fontFeatureSettings: `'tnum' 1`,
        },
      },
      MuiSvgIcon: {
        root: {
          fontSize: '2.8rem',
        },

        fontSizeSmall: {
          fontSize: '2.4rem',
        },

        fontSizeLarge: {
          fontSize: '3.2rem',
          margin: '0.2rem',
        },
      },
      MuiDivider: {
        root: {
          backgroundColor: colors.borderLight,
        },
      },
      MuiPaper: {
        outlined: { borderColor: colors.borderLight },
      },
      MuiFab: {
        root: {
          '&$disabled': {
            boxShadow: materialBoxShadows.six,
          },
        },
      },
    },
  });

  // overriding after creation in order to not supply all shadows.
  // https://github.com/mui-org/material-ui/issues/8780
  theme.shadows[1] = materialBoxShadows.one;
  theme.shadows[2] = materialBoxShadows.two;
  theme.shadows[3] = materialBoxShadows.three;
  theme.shadows[4] = materialBoxShadows.four;
  theme.shadows[5] = materialBoxShadows.five;
  theme.shadows[6] = materialBoxShadows.six;

  return theme;
};
